import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { NumericFormat } from "react-number-format";
import axios from "../axiosConfig";
import { toast } from "react-toastify";
import { ConfigProvider, Form } from "antd";
import vi_VN from "antd/locale/vi_VN";
import dayjs from "dayjs";
dayjs.locale("vi_VN");

const OfficeForm = () => {
  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [wards, setWards] = useState([]);
  const [selectedProvince, setSelectedProvince] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedWard, setSelectedWard] = useState("");
  const [detailedAddress, setDetailedAddress] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPositionId, setSelectedPositionId] = useState(null);
  const [positions, setPositions] = useState([]);
  const [errors, setErrors] = useState({});
  const [cvFile, setCvFile] = useState(null);
  const [cvFileName, setCvFileName] = useState("");

  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [formData, setFormData] = useState({
    name: "",
    birthday: "",
    phone: "",
    sex: "",
    contact_address: "",
    email: "",
    position: "",
    hope_salary: "",
    time_can_be_work: "",
    married_status: "",
    height: "",
    weight: "",
    relatives_name: "",
    relatives_relationship: "",
    relatives_phone: "",
    info_questions: [
      { title: "Người tham khảo tại công ty gần nhất ?", answer: "", note: "" },
    ],
  });

  // Hàm chuẩn hóa họ tên: Viết hoa chữ cái đầu mỗi từ
  const capitalizeName = (name) => {
    return name
      .toLowerCase()
      .replace(/\s+/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Validate ngày sinh: không rỗng, đúng định dạng yyyy-mm-dd
  const validateBirthday = (birthday) => {
    if (!birthday) return "Vui lòng nhập ngày sinh.";
    // Định dạng yyyy-mm-dd
    if (!/^\d{4}-\d{2}-\d{2}$/.test(birthday))
      return "Ngày sinh không đúng định dạng.";
    const birthDate = new Date(birthday);
    const today = new Date();

    // Kiểm tra ngày sinh không được trong tương lai
    if (birthDate > today) return "Ngày sinh không được trong tương lai.";

    // Kiểm tra tuổi tối thiểu (18) và tối đa (65)
    const age = calculateAge(birthday);
    if (age < 18) return "Bạn phải đủ 18 tuổi để ứng tuyển.";
    if (age > 65) return "Tuổi không được vượt quá 65.";
    return "";
  };

  // Validate số điện thoại: 10 số, bắt đầu bằng 0
  const validatePhone = (phone) => {
    if (!phone) return "Vui lòng nhập số điện thoại.";
    if (!/^0\d{9}$/.test(phone))
      return "Số điện thoại phải đủ 10 số và bắt đầu bằng 0.";
    return "";
  };

  // Validate email
  const validateEmail = (email) => {
    if (!email) return "Vui lòng nhập email.";
    // Định dạng email cơ bản
    if (!/^\S+@\S+\.\S+$/.test(email)) return "Email không đúng định dạng.";
    return "";
  };

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;
    let newErrors = { ...errors };

    if (name === "name") {
      newValue = capitalizeName(value);
      if (!newValue) {
        newErrors.name = "Vui lòng nhập họ và tên.";
      } else {
        newErrors.name = "";
      }
    }
    if (name === "birthday") {
      newErrors.birthday = validateBirthday(value);
    }
    if (name === "phone") {
      // Chỉ cho phép nhập số, tối đa 10 ký tự
      newValue = value.replace(/\D/g, "").slice(0, 10);
      newErrors.phone = validatePhone(newValue);
    }
    if (name === "email") {
      newErrors.email = validateEmail(value);
    }

    setFormData((prevState) => ({
      ...prevState,
      [name]: newValue,
    }));
    setErrors(newErrors);
  };

  // Handle radio button changes
  const handleRadioChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const getAllAddress = () => {
    const provinceText =
      document.querySelector("#province option:checked")?.text || "";
    const districtText =
      document.querySelector("#district option:checked")?.text || "";
    const wardText = document.querySelector("#ward option:checked")?.text || "";
    const parts = [detailedAddress, wardText, districtText, provinceText].filter(part => part && part.trim() !== "");
    const allAddress = parts.join(", ");
    return allAddress || null;
  };

  const calculateAge = (birthday) => {
    const birthDate = new Date(birthday);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return age;
  };

  const handleAddressChange = (e) => {
    setDetailedAddress(e.target.value);
  };

  // Handle change for info_questions
  const handleChangeNote = (index, value) => {
    const newFormData = { ...formData };
    newFormData.info_questions[index].note = value;
    setFormData(newFormData);
  };

  // Handle CV file upload - chỉ lưu file vào state, không upload ngay
  const handleCvFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Kiểm tra kích thước file (tối đa 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error(
        "File CV quá lớn. Vui lòng chọn file có kích thước tối đa 10 MB."
      );
      return;
    }

    // Kiểm tra định dạng file
    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Vui lòng chọn file PDF hoặc Word (.pdf, .doc, .docx)");
      return;
    }

    setCvFile(file);
    setCvFileName(file.name);
  };

  const onFinish = async (values) => {
    setLoading(true);

    // Tạo FormData để gửi file cùng với form data
    const formDataToSend = new FormData();
    
    // Thêm file CV nếu có
    if (cvFile) {
      formDataToSend.append("cv", cvFile);
    }
    
    // Thêm các trường dữ liệu khác (chỉ gửi nếu có giá trị)
    if (values.name || formData.name) {
      formDataToSend.append("name", values.name || formData.name);
    }
    if (formData.birthday) {
      formDataToSend.append("birthday", formData.birthday);
    }
    if (values.phone || formData.phone) {
      formDataToSend.append("phone", values.phone || formData.phone);
    }
    if (values.sex || formData.sex) {
      formDataToSend.append("sex", values.sex || formData.sex);
    }
    if (values.email || formData.email) {
      formDataToSend.append("email", values.email || formData.email);
    }
    if (selectedPositionId) {
      formDataToSend.append("recuitment_position_id", selectedPositionId);
    }
    const address = getAllAddress();
    if (address) {
      formDataToSend.append("contact_address", address);
      formDataToSend.append("temporary_address", address);
    }
    
    if (values.hope_salary) {
      const hopeSalary = typeof values.hope_salary === "string"
        ? Number(values.hope_salary.replace(/,/g, ""))
        : values.hope_salary;
      formDataToSend.append("hope_salary", hopeSalary);
    }
    
    if (values.time_can_be_work) {
      formDataToSend.append("time_can_be_work", values.time_can_be_work);
    }
    
    if (values.married_status) {
      formDataToSend.append("married_status", values.married_status);
    }
    
    if (values.height) {
      formDataToSend.append("height", values.height);
    }
    
    if (values.weight) {
      formDataToSend.append("weight", values.weight);
    }
    
    if (values.relatives_name) {
      formDataToSend.append("relatives_name", values.relatives_name);
    }
    
    if (values.relatives_relationship) {
      formDataToSend.append("relatives_relationship", values.relatives_relationship);
    }
    
    if (values.relatives_phone) {
      formDataToSend.append("relatives_phone", values.relatives_phone);
    }
    
    if (formData.info_questions && formData.info_questions.length > 0) {
      formDataToSend.append("info_questions", JSON.stringify(formData.info_questions));
    }

    console.log("Form data to send:", Object.fromEntries(formDataToSend));

    try {
      const response = await axios.post(
        "https://api.comnieuthienly.vn/api/form-cv-office",
        formDataToSend,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      setLoading(false);
      toast.success("Gửi form thành công!");
      navigate("/success");
      console.log("Response:", response.data);
    } catch (error) {
      console.error("Error:", error);
      setLoading(false);
      setError(error.response?.data?.msg || "Có lỗi xảy ra khi gửi form.");
      toast.error(error.response?.data?.msg || "Có lỗi xảy ra khi gửi form.");
      setTimeout(() => setError(null), 3000);
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Form validation failed:", errorInfo);
    const firstErrorField = errorInfo.errorFields[0]?.name[0];
    if (firstErrorField) {
      form.scrollToField(firstErrorField);
    }
  };

  // Fetch Tỉnh/Thành
  useEffect(() => {
    setLoading(true);
    axios
      .get("https://main.amhapy.com/api/place/vn/province")
      .then((response) => {
        const provinces = response?.data?.data || [];
        provinces.sort((a, b) => {
          if (a.id === 1) return -1; // Hà Nội first
          if (b.id === 1) return 1;
          if (a.id === 79) return -1; // Hồ Chí Minh second
          if (b.id === 79) return 1;
          return 0;
        });
        setProvinces(provinces);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching provinces:", error);
        setError("Có lỗi xảy ra khi lấy danh sách tỉnh/thành phố.");
        setLoading(false);
      });
  }, []);

  // Fetch Quận/Huyện khi tỉnh/thành thay đổi
  useEffect(() => {
    if (selectedProvince) {
      setLoading(true);
      axios
        .get(
          `https://main.amhapy.com/api/place/vn/district/${selectedProvince}`
        )
        .then((response) => {
          setDistricts(response?.data?.data || []);
          setSelectedDistrict(""); // Reset selected district
          setWards([]); // Reset wards
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching districts:", error);
          setError("Có lỗi xảy ra khi lấy danh sách quận/huyện.");
          setLoading(false);
        });
    } else {
      setDistricts([]);
      setWards([]);
    }
  }, [selectedProvince]);

  const convertGender = (gender) => {
    if (gender === "Khác") {
      return 0;
    }
    if (gender === "Nam") {
      return 1;
    }
    if (gender === "Nữ") {
      return 2;
    }
  };

  // Fetch Xã/Phường khi quận/huyện thay đổi
  useEffect(() => {
    if (selectedDistrict) {
      setLoading(true);
      axios
        .get(`https://main.amhapy.com/api/place/vn/wards/${selectedDistrict}`)
        .then((response) => {
          setWards(response?.data?.data || []);
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching wards:", error);
          setError("Có lỗi xảy ra khi lấy danh sách xã/phường.");
          setLoading(false);
        });
    } else {
      setWards([]);
    }
  }, [selectedDistrict]);

  useEffect(() => {
    setLoading(true);
    axios
      .get("https://api.comnieuthienly.vn/api/office-positions")
      .then((response) => {
        const data = response?.data?.data || [];
        setPositions(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching data:", err);
        setError("Có lỗi xảy ra khi lấy dữ liệu.");
        setLoading(false);
      });
  }, []);

  return (
    <div className="">
      <div className="flex flex-col items-center justify-center mb-8">
        <div className="card-banner banner-img p-style"></div>
        <ConfigProvider locale={vi_VN}>
          <Form
            className="p-full flex flex-col items-center justify-center"
            form={form}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
          >
            <div className="wrap-content p-style">
              <div className="line"></div>
              <div className="text">
                <span className="text-heading">
                  Xin chào Bạn, Đây là mẫu đơn ứng tuyển dành cho các vị trí trong
                  khối Office "CƠM NIÊU THIÊN LÝ"
                </span>
                <div className="line-top"></div>
                <p className="text-requied">* Chỉ ra những câu hỏi bắt buộc</p>
              </div>
            </div>
              {/* I. THÔNG TIN CHUNG */}
              <div className="wrap-content p-style">
                <div
                  className="line"
                  style={{ padding: "12px 24px", height: "auto" }}
                >
                  <span
                    className="text-content"
                    style={{ color: "rgb(0, 0, 0)", fontSize: "12pt" }}
                  >
                    I. THÔNG TIN CHUNG
                  </span>
                </div>
                <div className="text-content" style={{ marginTop: "50px" }}>
                  {/* Họ và tên */}
                  <div className="form-group flex justify-between items-center">
                    <label
                      className="text-style display--block mg"
                      htmlFor="name"
                    >
                      Họ Và Tên
                      <span
                        className="style-required"
                        aria-label="Required question"
                      >
                        *
                      </span>
                    </label>
                    <Form.Item
                      name="name"
                      className="w-full"
                      rules={[
                        {
                          required: true,
                          message: "Vui lòng nhập họ và tên",
                        },
                      ]}
                    >
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="Nhập họ và tên"
                      />
                    </Form.Item>
                  </div>
                  {errors.name && (
                    <div style={{ color: "red", fontSize: 13 }}>
                      {errors.name}
                    </div>
                  )}

                  {/* SĐT liên hệ */}
                  <div className="form-group flex justify-between items-center">
                    <label
                      className="text-style display--block mg"
                      htmlFor="phone"
                    >
                      Số Điện Thoại
                      <span
                        className="style-required"
                        aria-label="Required question"
                      >
                        {" "}
                        *
                      </span>
                    </label>
                    <Form.Item
                      name="phone"
                      className="w-full"
                      rules={[
                        {
                          required: true,
                          message: "Vui lòng nhập số điện thoại",
                        },
                        {
                          pattern: /^[0-9]{9,10}$/,
                          message: "Vui lòng nhập số điện thoại hợp lệ",
                        },
                      ]}
                    >
                      <input
                        type="text"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="form-input"
                        pattern="0\d{9}"
                        placeholder="Nhập số điện thoại"
                        maxLength={10}
                      />
                    </Form.Item>
                  </div>
                  {errors.phone && (
                    <div style={{ color: "red", fontSize: 13 }}>
                      {errors.phone}
                    </div>
                  )}

                  {/* Giới tính */}
                  <div className="form-group flex justify-between items-center">
                    <label
                      className="text-style display--block mg"
                      htmlFor="sex"
                    >
                      Giới tính
                      <span
                        className="style-required"
                        aria-label="Required question"
                      >
                        {" "}
                        *
                      </span>
                    </label>
                    <Form.Item
                      name="sex"
                      className="w-full"
                      rules={[
                        {
                          required: true,
                          message: "Vui lòng chọn giới tính",
                        },
                      ]}
                    >
                      <div className="flex flex-row justify-start w--70">
                        <label className="mr-5 flex items-center">
                          <input
                            type="radio"
                            name="sex"
                            value={convertGender("Nam")}
                            checked={formData.sex === "1"}
                            onChange={handleRadioChange}
                          />
                          Nam
                        </label>
                        <label className="mr-5 flex items-center">
                          <input
                            type="radio"
                            name="sex"
                            value={convertGender("Nữ")}
                            checked={formData.sex === "2"}
                            onChange={handleRadioChange}
                          />
                          Nữ
                        </label>
                      </div>
                    </Form.Item>
                  </div>

                  {/* Ngày sinh */}
                  <div className="form-group flex justify-between items-center">
                    <label
                      className="text-style display--block mg"
                      htmlFor="birthday"
                    >
                      Ngày sinh
                      <span
                        className="style-required"
                        aria-label="Required question"
                      >
                        {" "}
                        *
                      </span>
                    </label>
                    <Form.Item
                      name="birthday"
                      className="w-full"
                      rules={[
                        {
                          required: true,
                          message: "Vui lòng nhập ngày sinh",
                        },
                      ]}
                    >
                      <input
                        type="date"
                        name="birthday"
                        value={formData.birthday}
                        onChange={handleChange}
                        className="form-input"
                      />
                    </Form.Item>
                  </div>
                  {errors.birthday && (
                    <div style={{ color: "red", fontSize: 13 }}>
                      {errors.birthday}
                    </div>
                  )}

                  {/* Email */}
                  <div className="form-group flex justify-between items-center">
                    <label
                      className="text-style display--block mg"
                      htmlFor="email"
                    >
                      Email
                      <span
                        className="style-required"
                        aria-label="Required question"
                      >
                        {" "}
                        *
                      </span>
                    </label>
                    <Form.Item
                      name="email"
                      className="w-full"
                      rules={[
                        {
                          required: true,
                          message: "Vui lòng nhập email",
                        },
                      ]}
                    >
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="Nhập địa chỉ email"
                      />
                    </Form.Item>
                  </div>
                  {errors.email && (
                    <div style={{ color: "red", fontSize: 13 }}>
                      {errors.email}
                    </div>
                  )}
                </div>
              </div>

              {/* II. ĐỊA CHỈ TẠM TRÚ */}
              <div className="wrap-content p-style">
                <div
                  className="line"
                  style={{ padding: "12px 24px", height: "auto" }}
                >
                  <span
                    className="text-content"
                    style={{ color: "rgb(0, 0, 0)", fontSize: "12pt" }}
                  >
                    II. ĐỊA CHỈ TẠM TRÚ (ĐỊA CHỈ HIỆN TẠI)
                  </span>
                </div>
                <div className="text-content" style={{ marginTop: "50px" }}>
                  {/* Tỉnh/Thành */}
                  <div className="form-group">
                    <label
                      className="text-style display--block"
                      htmlFor="province"
                    >
                      Tỉnh/Thành
                      <span
                        className="style-required"
                        aria-label="Required question"
                      >
                        {" "}
                        *
                      </span>
                    </label>
                    <select
                      id="province"
                      className="form-input"
                      name="province"
                      value={selectedProvince}
                      onChange={(e) => setSelectedProvince(e.target.value)}
                      required
                    >
                      <option value="">Chọn Tỉnh/Thành</option>
                      {provinces.map((province) => (
                        <option key={province.id} value={province.id}>
                          {province.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Quận/Huyện */}
                  <div className="form-group">
                    <label
                      className="text-style display--block"
                      htmlFor="district"
                    >
                      Quận/Huyện
                      <span
                        className="style-required"
                        aria-label="Required question"
                      >
                        {" "}
                        *
                      </span>
                    </label>
                    <select
                      id="district"
                      className="form-input"
                      name="district"
                      value={selectedDistrict}
                      onChange={(e) => setSelectedDistrict(e.target.value)}
                      disabled={!selectedProvince}
                      required
                    >
                      <option value="">Chọn Quận/Huyện</option>
                      {districts.map((district) => (
                        <option key={district.id} value={district.id}>
                          {district.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Xã/Phường */}
                  <div className="form-group">
                    <label className="text-style display--block" htmlFor="ward">
                      Xã/Phường
                      <span
                        className="style-required"
                        aria-label="Required question"
                      >
                        {" "}
                        *
                      </span>
                    </label>
                    <select
                      id="ward"
                      className="form-input"
                      name="ward"
                      value={selectedWard}
                      onChange={(e) => setSelectedWard(e.target.value)}
                      disabled={!selectedDistrict}
                      required
                    >
                      <option value="">Chọn Xã/Phường</option>
                      {wards.map((ward) => (
                        <option key={ward.id} value={ward.id}>
                          {ward.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Địa chỉ chi tiết */}
                  <div className="form-group">
                    <label
                      className="text-style display--block"
                      htmlFor="detailedAddress"
                    >
                      Địa chỉ chi tiết
                      <span
                        className="style-required"
                        aria-label="Required question"
                      >
                        {" "}
                        *
                      </span>
                    </label>
                    <Form.Item
                      name="detailedAddress"
                      className="!w-full"
                      rules={[
                        {
                          required: true,
                          message: "Vui lòng địa chỉ tạm trú",
                        },
                      ]}
                    >
                      <textarea
                        type="text"
                        name="detailedAddress"
                        value={detailedAddress}
                        onChange={handleAddressChange}
                        className="form-input"
                        placeholder="Nhập địa chỉ chi tiết"
                      />
                    </Form.Item>
                  </div>
                </div>
              </div>

              {/* III. THÔNG TIN ỨNG TUYỂN */}
              <div className="wrap-content p-style">
                <div
                  className="line"
                  style={{ padding: "12px 24px", height: "auto" }}
                >
                  <span
                    className="text-content"
                    style={{ color: "rgb(0, 0, 0)", fontSize: "12pt" }}
                  >
                    III. THÔNG TIN ỨNG TUYỂN
                  </span>
                </div>
                <div className="text-content" style={{ marginTop: "50px" }}>
                  {/* Vị trí ứng tuyển */}
                  <div id="positionContainer" className="form-group">
                    <label className="text-style display--block">
                      Vị trí ứng tuyển
                      <span
                        className="style-required"
                        aria-label="Required question"
                      >
                        *
                      </span>
                    </label>
                    {positions?.map((item) => (
                      <label key={item.id} className="mr-5 flex items-center mb-2">
                        <input
                          type="radio"
                          name="position"
                          value={item.name}
                          checked={selectedPositionId === item.id}
                          onChange={(e) => {
                            setSelectedPositionId(item.id);
                            setFormData({
                              ...formData,
                              position: item.name,
                            });
                          }}
                          required
                        />
                        {item.name}
                      </label>
                    ))}
                  </div>

                  {/* Mức lương mong muốn */}
                  <div className="form-group flex justify-between items-center">
                    <label
                      className="text-style display--block mg"
                      htmlFor="hope_salary"
                    >
                      Mức lương mong muốn
                      <span
                        className="style-required"
                        aria-label="Required question"
                      >
                        *
                      </span>
                    </label>
                    <Form.Item
                      name="hope_salary"
                      className="w-full"
                      rules={[
                        {
                          required: true,
                          message: "Vui lòng nhập mức lương mong muốn",
                        },
                      ]}
                    >
                      <div className="input-container">
                        <NumericFormat
                          id="currency-input"
                          className="form-input"
                          thousandSeparator=","
                          decimalSeparator="."
                          allowNegative={false}
                          placeholder="Nhập số tiền"
                          customInput="input"
                          value={formData.hope_salary ?? ""}
                          onValueChange={(values) => {
                            setFormData({
                              ...formData,
                              hope_salary: values.value,
                            });
                          }}
                        />
                      </div>
                    </Form.Item>
                  </div>

                  {/* Thời gian có thể nhận việc */}
                  <div className="form-group flex justify-between items-center">
                    <label
                      className="text-style display--block mg"
                      htmlFor="time_can_be_work"
                    >
                      Thời gian có thể nhận việc
                      <span
                        className="style-required"
                        aria-label="Required question"
                      >
                        *
                      </span>
                    </label>
                    <Form.Item
                      name="time_can_be_work"
                      className="w-full"
                      rules={[
                        {
                          required: true,
                          message: "Vui lòng nhập thời gian có thể nhận việc",
                        },
                      ]}
                    >
                      <input
                        type="date"
                        name="time_can_be_work"
                        value={formData.time_can_be_work}
                        onChange={handleChange}
                        className="form-input"
                      />
                    </Form.Item>
                  </div>
                </div>
              </div>

              {/* IV. THÔNG TIN CÁ NHÂN BỔ SUNG */}
              <div className="wrap-content p-style">
                <div
                  className="line"
                  style={{ padding: "12px 24px", height: "auto" }}
                >
                  <span
                    className="text-content"
                    style={{ color: "rgb(0, 0, 0)", fontSize: "12pt" }}
                  >
                    IV. THÔNG TIN CÁ NHÂN BỔ SUNG
                  </span>
                </div>
                <div className="text-content" style={{ marginTop: "50px" }}>
                  {/* Tình trạng hôn nhân */}
                  <div className="form-group flex justify-between items-center">
                    <label
                      className="text-style display--block mg"
                      htmlFor="married_status"
                      style={{ marginTop: "15px" }}
                    >
                      Tình trạng hôn nhân
                      <span
                        className="style-required"
                        aria-label="Required question"
                      >
                        {" "}
                        *
                      </span>
                    </label>

                    <div className="flex justify-between items-center">
                      <Form.Item
                        name="married_status"
                        className="!w-full flex-1 ml-4"
                        rules={[
                          {
                            required: true,
                            message: "Vui lòng chọn tình trạng hôn nhân",
                          },
                        ]}
                      >
                        <div className="flex items-center">
                          <label className="mr-5 flex items-center">
                            <input
                              type="radio"
                              name="married_status"
                              value="0"
                              checked={formData.married_status === "0"}
                              onChange={handleRadioChange}
                            />
                            Độc thân
                          </label>
                          <label className="mr-5 flex items-center">
                            <input
                              type="radio"
                              name="married_status"
                              value="1"
                              checked={formData.married_status === "1"}
                              onChange={handleRadioChange}
                            />
                            Có gia đình
                          </label>
                          <label className="mr-5 flex items-center">
                            <input
                              type="radio"
                              name="married_status"
                              value="2"
                              checked={formData.married_status === "2"}
                              onChange={handleRadioChange}
                            />
                            Ly hôn
                          </label>
                        </div>
                      </Form.Item>
                    </div>
                  </div>

                  <div className="flex flex-wrap justify-between items-center">
                    {/* Chiều cao */}
                    <div className="form-group w--45">
                      <label
                        className="text-style display--block display--block"
                        htmlFor="height"
                      >
                        Chiều cao (cm)
                        <span
                          className="style-required"
                          aria-label="Required question"
                        >
                          {" "}
                          *
                        </span>
                      </label>
                      <Form.Item
                        name="height"
                        className="!w-full"
                        rules={[
                          {
                            required: true,
                            message: "Vui lòng nhập chiều cao",
                          },
                        ]}
                      >
                        <input
                          type="number"
                          name="height"
                          value={formData.height}
                          onChange={handleChange}
                          className="form-input"
                          placeholder="Nhập thông tin"
                        />
                      </Form.Item>
                    </div>

                    {/* Cân nặng */}
                    <div className="form-group w--45">
                      <label
                        className="text-style display--block"
                        htmlFor="weight"
                      >
                        Cân nặng (kg)
                        <span
                          className="style-required"
                          aria-label="Required question"
                        >
                          {" "}
                          *
                        </span>
                      </label>
                      <Form.Item
                        name="weight"
                        className="!w-full"
                        rules={[
                          {
                            required: true,
                            message: "Vui lòng nhập cân nặng",
                          },
                        ]}
                      >
                        <input
                          type="number"
                          name="weight"
                          value={formData.weight}
                          onChange={handleChange}
                          className="form-input"
                          placeholder="Nhập cân nặng của bạn"
                        />
                      </Form.Item>
                    </div>
                  </div>

                  {/* Người tham khảo */}
                  <div className="form-group">
                    <label
                      className="text-style display--block"
                      htmlFor="info_questions"
                    >
                      Người tham khảo (Quản lý tại công ty gần nhất: Họ tên + Chức vụ + SĐT liên hệ) (Nếu có)
                    </label>
                    {formData?.info_questions?.map((item, index) => (
                      <Form.Item
                        key={index}
                        name={item.title}
                        className="!w-full"
                        rules={[
                          {
                            required: false,
                          },
                        ]}
                      >
                        <textarea
                          type="text"
                          value={item.note}
                          onChange={(e) =>
                            handleChangeNote(index, e.target.value)
                          }
                          className="form-input"
                          placeholder="Nhập rõ Họ Tên, Chức vụ, Số Điện Thoại"
                        />
                      </Form.Item>
                    ))}
                  </div>

                  {/* Người báo tin trong trường hợp cần thiết */}
                  <div className="form-group">
                    <label
                      className="text-style display--block"
                      style={{ marginTop: "20px", marginBottom: "10px" }}
                    >
                      Người báo tin trong trường hợp cần thiết
                    </label>
                    <div className="flex flex-wrap justify-between items-center">
                      <div className="form-group w--45">
                        <label
                          className="text-style display--block"
                          htmlFor="relatives_name"
                        >
                          Họ và tên
                          <span
                            className="style-required"
                            aria-label="Required question"
                          >
                            {" "}
                            *
                          </span>
                        </label>
                        <Form.Item
                          name="relatives_name"
                          className="!w-full"
                          rules={[
                            {
                              required: true,
                              message: "Vui lòng nhập tên người báo tin",
                            },
                          ]}
                        >
                          <input
                            type="text"
                            name="relatives_name"
                            value={formData.relatives_name}
                            onChange={handleChange}
                            className="form-input"
                            placeholder="Nhập thông tin"
                          />
                        </Form.Item>
                      </div>
                      <div className="form-group w--45">
                        <label
                          className="text-style display--block"
                          htmlFor="relatives_relationship"
                        >
                          Mối quan hệ
                          <span
                            className="style-required"
                            aria-label="Required question"
                          >
                            {" "}
                            *
                          </span>
                        </label>
                        <Form.Item
                          name="relatives_relationship"
                          className="!w-full"
                          rules={[
                            {
                              required: true,
                              message:
                                "Vui lòng nhập mối quan hệ với người báo tin",
                            },
                          ]}
                        >
                          <input
                            type="text"
                            name="relatives_relationship"
                            value={formData.relatives_relationship}
                            onChange={handleChange}
                            className="form-input"
                            placeholder="Nhập thông tin"
                          />
                        </Form.Item>
                      </div>
                    </div>
                    <div className="flex flex-wrap justify-between items-center">
                      <div className="form-group w--45">
                        <label
                          className="text-style display--block"
                          htmlFor="relatives_phone"
                        >
                          Điện thoại
                          <span
                            className="style-required"
                            aria-label="Required question"
                          >
                            {" "}
                            *
                          </span>
                        </label>
                        <Form.Item
                          name="relatives_phone"
                          className="!w-full"
                          rules={[
                            {
                              required: true,
                              message:
                                "Vui lòng nhập số điện thoại người báo tin",
                            },
                            {
                              pattern: /^[0-9]{9,10}$/,
                              message: "Vui lòng nhập số điện thoại hợp lệ",
                            },
                          ]}
                        >
                          <input
                            type="tel"
                            name="relatives_phone"
                            value={formData.relatives_phone}
                            onChange={handleChange}
                            className="form-input"
                            placeholder="Nhập thông tin"
                          />
                        </Form.Item>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* V. TẢI CV UPDATE MỚI NHẤT */}
              <div className="wrap-content p-style">
                <div
                  className="line"
                  style={{ padding: "12px 24px", height: "auto" }}
                >
                  <span
                    className="text-content"
                    style={{ color: "rgb(0, 0, 0)", fontSize: "12pt" }}
                  >
                    V. TẢI CV UPDATE MỚI NHẤT
                  </span>
                </div>
                <div className="text-content" style={{ marginTop: "50px" }}>
                  <div className="form-group">
                    <label className="text-style display--block" htmlFor="cv">
                      File CV (PDF, DOC, DOCX - tối đa 10MB)
                    </label>
                    <input
                      type="file"
                      name="cv"
                      id="cv"
                      accept=".pdf,.doc,.docx"
                      onChange={handleCvFileChange}
                      className="form-input"
                    />
                    {cvFileName && (
                      <div style={{ marginTop: "10px", color: "green" }}>
                        Đã chọn: {cvFileName}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div style={{ marginTop: 20 }}>
                <div
                  className="flex flex-row content-start items-star justify-start submit--button"
                  style={{ width: "740px" }}
                >
                  <button style={{ marginRight: "25px" }}>
                    <Link to={"/"}>Trở lại</Link>
                  </button>
                  <button
                    htmlType="submit"
                    style={{ backgroundColor: "#e35522", color: "#fff" }}
                    disabled={loading}
                  >
                    Gửi
                  </button>
                  {loading && <div class="loader"></div>}
                </div>
              </div>
          </Form>
        </ConfigProvider>
        <a
          className="style-link flex flex-row items-center justify-center"
          href="https://comnieuthienly.vn/"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            style={{
              aspectRatio: "auto 74 / 24",
              width: "200px",
            }}
            src="https://i.imgur.com/nB8LOLM.png"
            alt="img-logo-thien-ly"
          />
        </a>
      </div>
      {/* Loading spinner */}
      <div id="loadingSpinner" className="hidden">
        Đang xử lý...
      </div>

      {/* Error notification */}
      <div id="errorPopup" className="hidden">
        <p>Đã xảy ra lỗi khi gửi dữ liệu. Vui lòng thử lại.</p>
      </div>
    </div>
  );
};

export default OfficeForm;
